(() => {
  if (window.__athomeCartDrawerEnhancementsLoaded) return;
  window.__athomeCartDrawerEnhancementsLoaded = true;

  let preservedScrollPosition = null;
  let discoveryObserver = null;

  const isReturnFocusTarget = (candidate) => (
    candidate instanceof HTMLElement
    && candidate !== document.body
    && candidate !== document.documentElement
    && candidate.isConnected
    && !candidate.hidden
    && !candidate.matches(':disabled, [aria-hidden="true"]')
  );

  const getReturnFocusTarget = (drawer) => {
    const candidates = [
      drawer.athomeReturnFocus,
      drawer.activeElement,
      document.querySelector('#cart-icon-bubble'),
    ];

    return candidates.find(isReturnFocusTarget) || null;
  };

  const focusWithoutScroll = (element) => {
    if (!element) return;

    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const captureDrawerScroll = (drawer) => {
    preservedScrollPosition = { x: window.scrollX, y: window.scrollY };
    drawer.athomeScrollX = preservedScrollPosition.x;
    drawer.athomeScrollY = preservedScrollPosition.y;
  };

  const restoreDrawerScroll = (drawer, release = false) => {
    const scrollX = preservedScrollPosition?.x ?? drawer.athomeScrollX;
    const scrollY = preservedScrollPosition?.y ?? drawer.athomeScrollY;
    if (!Number.isFinite(scrollY)) return;

    window.cancelAnimationFrame(drawer.athomeScrollFrame);
    drawer.athomeScrollFrame = window.requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
      if (release) {
        preservedScrollPosition = null;
        drawer.athomeScrollX = null;
        drawer.athomeScrollY = null;
      }
    });
  };

  const preserveDrawerScroll = (drawer) => {
    if (
      drawer.athomeScrollPatched
      || typeof drawer.open !== 'function'
      || typeof drawer.close !== 'function'
    ) return;

    const open = drawer.open;
    const close = drawer.close;
    drawer.open = function (triggeredBy, ...args) {
      const focusTarget = isReturnFocusTarget(triggeredBy) && !this.contains(triggeredBy)
        ? triggeredBy
        : isReturnFocusTarget(document.activeElement) && !this.contains(document.activeElement)
          ? document.activeElement
          : getReturnFocusTarget(this);

      if (focusTarget) {
        this.athomeReturnFocus = focusTarget;
        if (typeof this.setActiveElement === 'function') this.setActiveElement(focusTarget);
      }
      if (!Number.isFinite(preservedScrollPosition?.y)) captureDrawerScroll(this);
      const result = open.call(this, triggeredBy, ...args);
      restoreDrawerScroll(this);
      return result;
    };
    drawer.close = function (...args) {
      const returnFocus = getReturnFocusTarget(this);
      const result = close.apply(this, args);

      restoreDrawerScroll(this, true);
      window.cancelAnimationFrame(this.athomeFocusFrame);
      this.athomeFocusFrame = window.requestAnimationFrame(() => {
        if (!this.classList.contains('active')) focusWithoutScroll(returnFocus);
        this.athomeReturnFocus = null;
      });

      return result;
    };
    drawer.addEventListener('focusin', () => {
      if (drawer.classList.contains('active')) restoreDrawerScroll(drawer);
    });
    drawer.athomeScrollPatched = true;
  };

  const syncCartIcon = (itemCount = null) => {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;

    const count = itemCount === null
      ? [...document.querySelectorAll('cart-drawer .quantity__input')]
        .reduce((total, input) => total + Math.max(0, Number.parseInt(input.value, 10) || 0), 0)
      : Math.max(0, Number.parseInt(itemCount, 10) || 0);
    const countNode = cartIcon.querySelector('.ath-site-header__cart-count');
    if (countNode) countNode.textContent = String(count);
    cartIcon.setAttribute('aria-label', `Open cart with ${count} ${count === 1 ? 'item' : 'items'}`);
  };

  const syncProgressAccessibility = (drawer) => {
    drawer.querySelectorAll('.progress[role="progressbar"]').forEach((progress) => {
      const fill = progress.querySelector('.progress-bar');
      const widthMatch = fill?.style.width?.match(/^([0-9]+(?:\.[0-9]+)?)%$/);
      if (!widthMatch) return;

      const value = Math.min(100, Math.max(0, Number.parseFloat(widthMatch[1])));
      const headings = progress.closest('.blockProgressWrapper-1')?.querySelectorAll('.ProgressBarHeaddingText');
      const goal = headings?.[0]?.textContent?.trim() || 'Free shipping';
      const status = headings?.[1]?.textContent?.replace(/\s+/g, ' ').trim();

      progress.setAttribute('aria-valuenow', String(value));
      progress.setAttribute('aria-label', `${goal} progress`);
      if (value >= 100) {
        progress.setAttribute('aria-valuetext', `${goal} unlocked`);
      } else if (status) {
        progress.setAttribute('aria-valuetext', `${value}% complete; ${status}`);
      } else {
        progress.removeAttribute('aria-valuetext');
      }
    });
  };

  const initDrawer = () => {
    const drawers = document.querySelectorAll('cart-drawer');
    drawers.forEach((drawer) => {
      preserveDrawerScroll(drawer);
      if (drawer.classList.contains('active') && Number.isFinite(preservedScrollPosition?.y)) {
        restoreDrawerScroll(drawer);
      }
      if (drawer.athomeA11yObserver) return;

      let animationFrame;
      let wasActive = drawer.classList.contains('active');
      const scheduleSync = () => {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = window.requestAnimationFrame(() => {
          const isActive = drawer.classList.contains('active');
          if (isActive !== wasActive) {
            restoreDrawerScroll(drawer, !isActive);
            wasActive = isActive;
          }
          syncProgressAccessibility(drawer);
          syncCartIcon();
        });
      };

      drawer.athomeA11yObserver = new MutationObserver(scheduleSync);
      drawer.athomeA11yObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });

      scheduleSync();
    });

    return drawers.length;
  };

  const stopDrawerDiscovery = () => {
    discoveryObserver?.disconnect();
    discoveryObserver = null;
  };

  const discoverDrawer = () => {
    const drawerCount = initDrawer();
    if (drawerCount > 0) stopDrawerDiscovery();
    return drawerCount;
  };

  const startDrawerDiscovery = () => {
    if (discoverDrawer() > 0 || discoveryObserver) return;

    discoveryObserver = new MutationObserver(discoverDrawer);
    discoveryObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const captureTriggerScroll = (event, replace = false) => {
    const trigger = event.target.closest?.('#cart-icon-bubble, button[name="add"], .product-form__submit');
    if (!trigger || trigger.closest('cart-drawer')) return;

    const drawer = document.querySelector('cart-drawer');
    if (
      drawer
      && !drawer.classList.contains('active')
      && (replace || !Number.isFinite(preservedScrollPosition?.y))
    ) {
      captureDrawerScroll(drawer);
      drawer.athomeReturnFocus = trigger;
      if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
    }
  };

  document.addEventListener('pointerdown', (event) => captureTriggerScroll(event, true), true);
  document.addEventListener('click', (event) => captureTriggerScroll(event), true);

  document.addEventListener('click', (event) => {
    const continueButton = event.target.closest?.('.continue-shopping');
    if (!continueButton) return;
    event.preventDefault();
    const drawer = continueButton.closest('cart-drawer');
    if (typeof drawer?.close === 'function') drawer.close();
    else {
      drawer?.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startDrawerDiscovery, { once: true });
  } else {
    startDrawerDiscovery();
  }
  document.addEventListener('shopify:section:load', discoverDrawer);
  document.addEventListener('shopify:section:unload', (event) => {
    const drawers = event.target.matches?.('cart-drawer')
      ? [event.target]
      : event.target.querySelectorAll?.('cart-drawer') || [];

    drawers.forEach((drawer) => {
      drawer.athomeA11yObserver?.disconnect();
      window.cancelAnimationFrame(drawer.athomeScrollFrame);
      window.cancelAnimationFrame(drawer.athomeFocusFrame);
    });
  });
  window.customElements?.whenDefined('cart-drawer').then(discoverDrawer);

})();
